import { useEffect, useState } from 'react';
import { Inbox as InboxIcon, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Link } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { supabase } from '../../lib/supabase';
import styles from './inbox.module.css';

interface ConversationRow {
  id: string;
  phone: string;
  full_name: string | null;
  state: string;
  service_slug: string | null;
  last_message_at: string;
  fallback_count: number;
}

export function InboxPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    void (async () => {
      const { data } = await supabase
        .from('bot_conversations')
        .select('id, phone, full_name, state, service_slug, last_message_at, fallback_count')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('last_message_at', { ascending: false })
        .limit(100);
      if (cancel.current) return;
      setRows(data ?? []);
      setLoading(false);
    })();
    return () => { cancel.current = true; };
  }, [orgId]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>אינבוקס</h1>
          <p className={styles.sub}>כרגע מציג שיחות WhatsApp פעילות מהבוט.</p>
        </div>
        <InboxIcon size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      {loading ? (
        <p className={styles.notice}>טוען...</p>
      ) : rows.length === 0 ? (
        <div className={styles.emptyCard}>
          <MessageCircle size={64} className={styles.bigIcon} aria-hidden="true" />
          <h2>אין שיחות פעילות</h2>
          <p>שיחות WhatsApp מהבוט יופיעו כאן. ניתן לראות גם ב-<Link to="/bot/conversations">שיחות הבוט</Link>.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {rows.map((r) => (
            <Link key={r.id} to={`/bot/conversations/${r.id}`} className={styles.row}>
              <div className={styles.rowIcon} aria-hidden="true">
                <MessageCircle size={18} />
              </div>
              <div className={styles.rowMain}>
                <strong>{r.full_name ?? <span className={styles.muted}>(ללא שם)</span>}</strong>
                <span className={styles.rowMeta}>
                  <bdi dir="ltr">{r.phone}</bdi>
                  {r.service_slug ? <> · {r.service_slug}</> : null}
                  · <span className={styles.muted}>{r.state}</span>
                </span>
              </div>
              <div className={styles.rowTime}>
                {format(new Date(r.last_message_at), 'dd/MM HH:mm', { locale: he })}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
